import { appAlert, appPrompt } from '../utils/appDialog';
import { runOptimisticGithubWrite } from '../utils/optimisticGithubWrite';
import {
  addQuickListItemToFile,
  QUICK_LIST_TARGETS,
  type QuickListKind,
} from '../utils/quickListEntry';
import type { CheckItem } from '../types';
import type { AppContext } from './appContext';

export async function promptAndAddQuickListItem(ctx: AppContext, kind: QuickListKind): Promise<void> {
  const target = QUICK_LIST_TARGETS[kind];
  const text = await appPrompt('', {
    title: target.promptTitle,
    placeholder: target.placeholder,
    confirmLabel: 'Add',
    cancelLabel: 'Cancel',
  });
  if (!text?.trim()) return;
  await addQuickListItem(ctx, kind, text);
}

export async function addQuickListItem(
  ctx: AppContext,
  kind: QuickListKind,
  text: string
): Promise<void> {
  const target = QUICK_LIST_TARGETS[kind];
  const file = await ctx.fileRegistry.ensureLoaded(target.path);
  const section = file.sections.find(s => s.title === target.sectionTitle);
  if (!section) {
    await appAlert(`Could not find section "${target.sectionTitle}" in ${target.path}.`);
    return;
  }

  let original = ctx.fileRegistry.originalContents.get(target.path);
  if (original === undefined) {
    const { content } = await ctx.github.readFile(target.path);
    original = content;
    ctx.fileRegistry.originalContents.set(target.path, content);
  }

  const itemsSnapshot: CheckItem[] = section.items.map(item => ({ ...item }));
  const originalSnapshot = original;

  let item: CheckItem;
  try {
    item = addQuickListItemToFile(file, kind, text);
  } catch (err) {
    await appAlert((err as Error).message);
    return;
  }

  const serialized = ctx.markdown.serialize(file, original);
  const mainEl = ctx.root.querySelector<HTMLElement>('#main');
  const isOpenInEditor = ctx.fileView.getCurrentFilePath() === target.path;

  await runOptimisticGithubWrite({
    applyLocal: () => {
      ctx.fileRegistry.originalContents.set(target.path, serialized);
      ctx.invalidateSearchIndex();
      if (isOpenInEditor && mainEl) {
        ctx.fileView.render(mainEl, file);
      }
    },
    rollback: () => {
      section.items.length = 0;
      section.items.push(...itemsSnapshot);
      ctx.fileRegistry.originalContents.set(target.path, originalSnapshot);
      if (isOpenInEditor && mainEl) {
        ctx.fileView.render(mainEl, file);
      }
    },
    write: async () => {
      await ctx.github.writeFile(
        target.path,
        serialized,
        file.sha,
        `App: add ${kind} — ${item.text.slice(0, 60)}`
      );
    },
    onSuccess: async () => {
      await ctx.fileRegistry.refreshSha(file);
    },
    onFailure: async err => {
      await appAlert(`Could not save ${kind} item: ${err.message}`);
    },
  });
}
